import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from '../../../components/common/Tips'
import EditModal from '../../../components/setting/record/EditModal'
import { sortBy } from 'lodash'
import { compile } from '../../../../utils/search'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { fetchSettingWeeklyTrackTime, updateSettingWeeklyTrackTime } from '../../../../states/containers/setting/systemSlice'
import { RequestPatchSettingWeeklyTrackTime, SettingWeeklyTrackTimeMember } from '../../../../states/types/setting'
import { RootState } from '../../../../states/containers/store'

interface weeklyTrackTime {
  week_number: number
  track_start_time: string | null
  track_stop_time: string | null
}

interface RequestParams {
  commonFlg: boolean
  memberIds?: number[]
  changeAuthority?: boolean
  weeklyTrackTimes?: weeklyTrackTime[]
  useCommonSetting? : boolean
}

const DISPLAY_BLOCK_NUMBER = 100

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [membersInit, setMembersInit] = useState<SettingWeeklyTrackTimeMember[]>([])
  const [members, setMembers] = useState<SettingWeeklyTrackTimeMember[]>([])
  const [checkedMemberIds, setCheckedMemberIds] = useState<number[]>([])
  const [searchWord, setSearchWord] = useState<string>("")
  const [composition, setComposition] = useState<boolean>(false)

  const [commonValue, setCommonValue] = useState<weeklyTrackTime[]>([])

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<SettingWeeklyTrackTimeMember[]>([])

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && members.length >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, members])

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(membersInit.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const changeWeeklyTrackTime = (data: RequestParams) => {
    const param: RequestPatchSettingWeeklyTrackTime = { change_common_setting: data.commonFlg }
    if (data.commonFlg) {
      param.weekly_track_times = data.weeklyTrackTimes
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = data.useCommonSetting
      if (data.weeklyTrackTimes && !data.useCommonSetting) param.weekly_track_times = data.weeklyTrackTimes
      if (data.changeAuthority !== null) param.change_authority = data.changeAuthority
    }
    dispatch(updateSettingWeeklyTrackTime(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          if (data.weeklyTrackTimes) setCommonValue(data.weeklyTrackTimes)
          alert.success("会社全体の設定を変更しました")
        } else {
          let tmpWeeklyTrackTimes: weeklyTrackTime[] | null = null
          if (param.use_common_setting) {
            tmpWeeklyTrackTimes = commonValue
          } else if (param.weekly_track_times) {
            tmpWeeklyTrackTimes = param.weekly_track_times
          }
          setMembers((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? {
                ...v,
                use_common_setting: param.use_common_setting ?? v.use_common_setting,
                weekly_track_times: tmpWeeklyTrackTimes ?? v.weekly_track_times,
                change_authority: param.change_authority ?? v.change_authority,
              } : v
          )))
          setMembersInit((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? {
                ...v,
                use_common_setting: param.use_common_setting ?? v.use_common_setting,
                weekly_track_times: tmpWeeklyTrackTimes ?? v.weekly_track_times,
                change_authority: param.change_authority ?? v.change_authority,
              } : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  const clickCheckbox = (memberId: number) => {
    if (checkedMemberIds.includes(memberId)) {
      setCheckedMemberIds(checkedMemberIds.filter((v) => v !== memberId))
    } else {
      setCheckedMemberIds([...checkedMemberIds, memberId])
    }
  }

  const reset = () => {
    setSearchWord("")
    setMembers(membersInit)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const search = (originalList: SettingWeeklyTrackTimeMember[] | undefined) => {
    if (!searchWord) {
      reset()
      setDisplayBlockNum(1)
      return
    }
    const word = searchWord.split(/\s+/)
    const list = originalList?.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name + member.kana + member.email + member.code + member.team_name))
    }) as SettingWeeklyTrackTimeMember[]
    setMembers(sortBy(list, 'id'))
    const memberIds = list.map((v) => v.id)
    setCheckedMemberIds((cur) => cur.filter((v) => memberIds.includes(v)))
    setDisplayBlockNum(1)
  }

  const displayTime = (value: {week_number: number, track_start_time: string | null, track_stop_time: string | null}) => {
    if ((value.track_start_time === value.track_stop_time) && (value.track_start_time !== null && value.track_stop_time !== null)) {
      return (
        <div
          style={{
            display: "flex",
            flexFlow: "column",
            gap: "2px",
            color: "#FF0000",
          }}
        >
          <Tips
            description="計測しない"
            className=""
            icon={false}
          >
            <div style={{ fontSize: "16px" }}>
              <FontAwesomeIcon icon={['fas', 'times']} />
            </div>
          </Tips>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "2px",
            }}
          >
            計測しない
          </div>
        </div>
      )
    }
    if ((value.track_start_time === null) && (value.track_stop_time === null)) {
      return (
        <div
          style={{
            display: "flex",
            flexFlow: "column",
            gap: "2px",
            color: "#0f9655",
          }}
        >
          <Tips
            description="終日計測する"
            className=""
            icon={false}
          >
            <div style={{ fontSize: "16px" }}>
              <FontAwesomeIcon icon={['fas', 'play-circle']} />
            </div>
          </Tips>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "2px",
            }}
          >
            <div>
              0:00
            </div>
            <div>
              ~
            </div>
            <div>
              24:00
            </div>
          </div>
        </div>
      )
    }
    return (
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "2px",
          color: "#666666",
        }}
      >
        <Tips
          description="計測制限あり"
          className=""
          icon={false}
        >
          <div style={{ fontSize: "16px" }}>
            <FontAwesomeIcon icon={['far', 'clock']} />
          </div>
        </Tips>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "2px",
          }}
        >
          <div>
            {value.track_start_time ? value.track_start_time.substring(0, 5) : "0:00"}
          </div>
          <div>
            ~
          </div>
          <div>
            {value.track_stop_time ? value.track_stop_time.substring(0, 5) : "24:00"}
          </div>
        </div>
      </div>
    )
  }

  const membersList = (member_array: SettingWeeklyTrackTimeMember[]) => (
    member_array && member_array.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((v) => (
      <tr
        key={v.id}
        onClick={() => clickCheckbox(v.id)}
        style={{
          backgroundColor: checkedMemberIds.includes(v.id) ? "#f3fff9" : "",
          cursor: "pointer",
        }}
      >
        <td>
          <div
            className={`check_box ${checkedMemberIds.includes(v.id) ? "checked" : ""}`}
          >
            {
              checkedMemberIds.includes(v.id) ? (
                <FontAwesomeIcon icon={['far', 'check-square']} />
              ) : (
                <FontAwesomeIcon icon={['far', 'square']} />
              )
            }
          </div>
        </td>
        <td>
          <div className="track_setting">
            <Tips
              description="編集"
              className=""
              icon={false}
            >
              <button
                onClick={(e) => {
                  openUpdateModal([v.id])
                  e.stopPropagation()
                }}
                className="edit"
                type="button"
              >
                <FontAwesomeIcon icon={['fas', 'tools']} />
              </button>
            </Tips>
          </div>
        </td>
        <td>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "4px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <img
                alt={v.name}
                src={v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                style={{ width: "16px", height: "16px", borderRadius: "50%" }}
              />
              <ruby>
                {v.name}
                <rt>{v.kana}</rt>
              </ruby>
              {v.code ? ` (${v.code})` : ""}
              {
                v.active || (
                  <Tips
                    description="休眠中💤"
                    className=""
                    icon={false}
                    style={{
                      position: "relative",
                    }}
                  >
                    <>
                      💤
                    </>
                  </Tips>
                )
              }
            </div>
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "2px",
                border: "solid 1px #999999",
                borderRadius: "4px",
                padding: "4px",
                backgroundColor: "#FFFFFF",
                color: "#666666",
                fontSize: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <FontAwesomeIcon icon={['far', 'envelope']} fixedWidth />
                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                    minWidth: 0,
                    textAlign: "left",
                  }}
                >
                  {v.email}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <FontAwesomeIcon icon={['fas', 'users']} fixedWidth />
                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                    minWidth: 0,
                    textAlign: "left",
                  }}
                >
                  {v.team_name || "所属チームなし"}
                </div>
              </div>
            </div>
          </div>
        </td>
        <td>
          <div
            style={{
              fontSize: "20px",
              color: v.change_authority ? "#0f9655" : "#FF0000",
            }}
          >
            <FontAwesomeIcon icon={['far', v.change_authority ? 'check-circle' : 'times-circle']} />
            <div
              style={{
                fontSize: "10px",
              }}
            >
              {v.change_authority ? "許可する" : "許可しない"}
            </div>
          </div>
        </td>
        <td
          colSpan={7}
          style={{
            width: "100%",
            padding: "2px 0",
            position: "relative",
          }}
        >
          {
            v.use_common_setting && (
              <div
                style={{
                  fontSize: "10px",
                  padding: "4px 8px",
                  margin: "2px auto",
                  backgroundColor: "#076ad0",
                  color: "#FFFFFF",
                  borderRadius: "4px",
                  width: "fit-content",
                  position: "absolute",
                  top: "0px",
                  left: "0px",
                }}
              >
                会社全体の設定に準拠する
              </div>
            )
          }
          <div
            style={{
              display: "flex",
              border: v.use_common_setting ? "solid 1px #076ad0" : "none",
              borderRadius: v.use_common_setting ? "4px" : "0",
              marginTop: v.use_common_setting ? "12px" : "0",
              backgroundColor: v.use_common_setting ? "#f0f8ff" : "transparent",
            }}
          >
            {
              (!v.use_common_setting ? v.weekly_track_times : commonValue).map((w) => (
                <div
                  key={w.week_number}
                  style={{
                    padding: "8px 4px",
                    width: "calc(100% / 7)",
                  }}
                >
                  {displayTime(w)}
                </div>
              ))
            }
          </div>
        </td>
      </tr>
    ))
  )

  const buildList = useMemo(() => members && membersList(members), [members, membersInit, checkedMemberIds, commonValue, displayBlockNum])

  const legend = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        padding: "8px 16px",
        border: "solid 1px #CCCCCC",
        backgroundColor: "#FAFAFA",
        marginBottom: "8px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          color: "#666666",
        }}
      >
        凡例
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "right",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "11px",
          }}
        >
          <div style={{ color: "#0f9655" }}>
            <FontAwesomeIcon icon={['fas', 'play-circle']} />
          </div>
          <div>
            :
          </div>
          終日計測する
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "11px",
          }}
        >
          <div style={{ color: "#FF0000" }}>
            <FontAwesomeIcon icon={['fas', 'times']} />
          </div>
          <div>
            :
          </div>
          計測しない
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: "11px",
          }}
        >
          <div style={{ color: "#777777" }}>
            <FontAwesomeIcon icon={['far', 'clock']} />
          </div>
          <div>
            :
          </div>
          計測制限あり
        </div>
      </div>
    </div>
  )

  useEffect(() => {
    dispatch(
      fetchSettingWeeklyTrackTime(
        { only_oneself: false },
        (result) => {
          setMembers(result.members)
          setMembersInit(result.members)
          setCommonValue(result.common.weekly_track_times)
        },
      ),
    )
  }, [])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 計測時間設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        計測時間設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>計測時間設定</h1>
      </section>
      <section className="page_description">
        <p>
          Qaseeで計測する時間を設定することができます。
        </p>
        <p>
          ユーザーを指定して個別に設定することもできます。
        </p>
      </section>
      {
        login.role_target_restriction || (
          <section className="page_contents content-area">
            <div className="content-block">
              <div className="block-detail">
                <div className="header_area measurement">
                  <div className="title">会社全体の設定</div>
                  <button
                    type="button"
                    className="edit_button"
                    onClick={() => openUpdateModal([])}
                  >
                    会社の基本設定を編集
                  </button>
                </div>
                {legend}
                <table className="company_track_table">
                  <thead>
                    <tr>
                      <th style={{ color: "#FF0000" }}>日曜日</th>
                      <th>月曜日</th>
                      <th>火曜日</th>
                      <th>水曜日</th>
                      <th>木曜日</th>
                      <th>金曜日</th>
                      <th style={{ color: "#317AB7" }}>土曜日</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {
                        commonValue.map((v) => (
                          <td key={v.week_number}>
                            {displayTime(v)}
                          </td>
                        ))
                      }
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )
      }
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            <div className="header_area measurement">
              <div className="title">ユーザーの個別設定</div>
              <div className="edit_button_area">
                <div className="search_area">
                  <input
                    type="text"
                    onChange={(event) => setSearchWord(event.target.value)}
                    onBlur={() => search(membersInit)}
                    onKeyDown={(event) => keyDown(event)}
                    onCompositionStart={() => setComposition(true)}
                    onCompositionEnd={() => setComposition(false)}
                    value={searchWord}
                    placeholder="名前・メールアドレス・チーム名でユーザー検索"
                  />
                  {
                    searchWord && (
                      <button type="button" className="reset" onClick={reset}>
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
              </div>
            </div>
            <div
              style={{
                border: `solid 1px ${checkedMemberIds.length ? "#228b22" : "#999999"}`,
                borderRadius: "4px",
                backgroundColor: checkedMemberIds.length ? "#f3fff9" : "#EEEEEE",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "right",
                gap: "12px",
                marginBottom: "16px",
                position: "sticky",
                top: "60px",
                zIndex: "1",
              }}
            >
              {
                checkedMemberIds.length ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "2px",
                      }}
                    >
                      <div
                        style={{
                          color: "#228b22",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {checkedMemberIds.length}
                      </div>
                      <div>
                        名のユーザーが選択されています
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openUpdateModal(checkedMemberIds)}
                      style={{
                        border: "1px solid #228b22",
                        borderRadius: "4px",
                        backgroundColor: "#228b22",
                        color: "#FFFFFF",
                        display: "flex",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={["fas", "check"]}
                        fixedWidth
                      />
                      まとめて変更する
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        marginTop: "8px",
                      }}
                    >
                      ユーザーが選択されていません
                    </div>
                    <button
                      type="button"
                      disabled
                      style={{
                        border: "1px solid #DDDDDD",
                        borderRadius: "4px",
                        backgroundColor: "#DDDDDD",
                        color: "#999999",
                        display: "flex",
                        gap: "4px",
                        cursor: "not-allowed",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={["fas", "check"]}
                        fixedWidth
                      />
                      まとめて変更する
                    </button>
                  </>
                )
              }
            </div>
            {legend}
            <table className="weekly_track_table">
              <thead>
                <tr
                  className="total_table_header"
                >
                  <th rowSpan={2}>
                    <div
                      className={`check_box ${checkedMemberIds.length === members?.length ? "checked" : ""}`}
                      style={{
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "8px",
                          color: "#999999",
                          whiteSpace: "nowrap",
                          position: "absolute",
                          top: "-10px",
                          left: "50%",
                          transform: "translateX(-50%)",
                        }}
                      >
                        全選択
                      </div>
                      {
                        checkedMemberIds.length === members?.length ? (
                          <FontAwesomeIcon icon={['far', 'check-square']} onClick={() => setCheckedMemberIds([])} />
                        ) : (
                          <FontAwesomeIcon icon={['far', 'square']} onClick={() => setCheckedMemberIds(members ? members.map((v) => v.id) : [])} />
                        )
                      }
                    </div>
                  </th>
                  <th rowSpan={2}>操作</th>
                  <th rowSpan={2}>
                    名前
                  </th>
                  <th rowSpan={2} style={{ whiteSpace: "nowrap" }}>
                    <Tips
                      description="自分自身で計測時間設定を変更することができるようになります"
                      className=""
                    >
                      <>
                        編集許可
                      </>
                    </Tips>
                  </th>
                  <th colSpan={7}>計測時間</th>
                </tr>
                <tr className="week_header">
                  <th style={{ color: "#FF0000" }}>日曜日</th>
                  <th>月曜日</th>
                  <th>火曜日</th>
                  <th>水曜日</th>
                  <th>木曜日</th>
                  <th>金曜日</th>
                  <th style={{ color: "#317AB7" }}>土曜日</th>
                </tr>
              </thead>
              <tbody>
                {buildList}
              </tbody>
            </table>
          </div>
        </div>
        <EditModal
          targetMemberList={targetMembers}
          commonValues={commonValue}
          isDisplayModal={isDisplayModal}
          modalType="multi"
          submitCallback={(data: RequestParams) => changeWeeklyTrackTime(data)}
          closeCallback={closeModal}
        />
      </section>
    </section>
  )
}

export default Index
